import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { APPDNS, APPNAME, APPCREATOR } from "../utils/utils.constants";
import { routes } from "../utils/utils.routes";
import { SocialLinks } from "./subcomponents/SocialLinks";
import { apiPost } from "../utils/api";
import { message } from "antd";

type FooterLink = {
  href: string;
  label: string;
  isExternal?: boolean;
};

const FooterColumn: React.FC<{
  title: string;
  links: FooterLink[];
}> = ({ title, links }) => {
  const { pathname } = useLocation();

  return (
    <nav className="site-footer__col" aria-labelledby={`footer-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h3 className="site-footer__col-title" id={`footer-${title.replace(/\s+/g, "-").toLowerCase()}`}>
        {title}
      </h3>
      <ul className="site-footer__menu-list">
        {links.map((link) => {
          const isCurrent = !link.isExternal && pathname === link.href;

          return (
            <li
              key={link.href + link.label}
              className={`site-footer__menu-item${isCurrent ? " site-footer__menu-item--current" : ""}`}
            >
              {link.isExternal ? (
                <a
                  href={link.href}
                  className="site-footer__menu-link"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.href}
                  className="site-footer__menu-link"
                  {...(isCurrent && { "aria-current": "page" })}
                >
                  {link.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export const Footer: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await apiPost("/newsletter", { email: email.trim() });
      message.success(res.message || "Inscription réussie");
      setEmail("");
    } catch (err: any) {
      message.error(err?.response?.data?.reason || err?.messageText || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="site-footer">
      <div className="site-footer__accent" aria-hidden="true" />
      <div className="site-footer__inner has-header-footer-background-color has-global-padding px-4">
        <div className="site-footer__grid">
          <div className="site-footer__col site-footer__col--brand">
            <p className="site-footer__brand-name page-section-header__title text-white">{APPNAME}</p>
            <p className="site-footer__brand-text">
              Université Libre des Pays des Grands Lacs — une université chrétienne des Églises protestantes des Pays des Grands Lacs, privée et agréée par le gouvernement congolais.
            </p>
            <div className="site-footer__social">
              <p className="site-footer__social-label">Suivez-nous</p>
              <SocialLinks />
            </div>
          </div>

          <FooterColumn
            title="Liens rapides"
            links={[
              { href: routes.HOME, label: "Page d'accueil" },
              { href: routes.ABOUT, label: "À propos" },
              { href: routes.FACULTIES, label: "Facultés" },
              { href: routes.DOMAINES, label: "Domaines" },
              { href: routes.METANOIA, label: "Métanoia" },
              { href: routes.CONTACTS, label: "Contacts" },
            ]}
          />

          <FooterColumn
            title="Ressources"
            links={[
              { href: routes.ARTICLES, label: "Blog et Articles" },
              { href: routes.CENTRES, label: "Centres de recherche" },
              { href: routes.SEARCH, label: "Recherche" },
              { href: routes.ANCIENS, label: "Anciens de l'" + APPNAME, isExternal: true },
              { href: routes.COMPLAINTS, label: "Plaintes", isExternal: true },
              { href: routes.LIBRARY, label: "Bibliothèque", isExternal: true },
              { href: routes.LOGIN, label: "Admin site" },
            ]}
          />

          <div className="site-footer__col site-footer__col--newsletter">
            <h3 className="site-footer__col-title">Newsletter</h3>
            <p className="site-footer__newsletter-desc">
              Recevez chaque semaine nos actualités et les dernières publications du campus.
            </p>
            <form
              className="site-footer__newsletter-form"
              onSubmit={subscribe}
              data-testid="newsletter-form"
            >
              <label className="site-footer__newsletter-field">
                <span className="screen-reader-text">Adresse e-mail</span>
                <input
                  className="site-footer__newsletter-input"
                  data-testid="newsletter-email-input"
                  type="email"
                  placeholder="votre@email.com"
                  name="mail"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <button
                  className="site-footer__newsletter-btn"
                  data-testid="newsletter-submit-button"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "…" : "Souscrire"}
                </button>
              </label>
              <p className="site-footer__newsletter-hint">
                Pas de spam. Désinscription possible à tout moment.
              </p>
            </form>
          </div>
        </div>

        {/* {visits && (
          <div className="site-footer__visits" data-testid="footer-visit-counter">
            <UsersI aria-hidden size={16} />
            <span className="site-footer__visits-text">
              <strong>{visits.total.toLocaleString("fr-FR")}</strong> visites
              {" · "}
              <strong>{visits.unique.toLocaleString("fr-FR")}</strong> visiteurs
            </span>
          </div>
        )} */}

        <div className="site-footer__bottom">
          <p className="site-footer__copyright">
            © {new Date().getFullYear()} {APPNAME}. Tous droits réservés.
          </p>
          <p className="site-footer__credit">
            Conception{" "}
            <a href={APPDNS} className="site-footer__credit-link" target="_blank" rel="noreferrer noopener">
              {APPCREATOR}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
